// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import type { Incident, Outcome } from '../types';
import {
  OUTCOME_COLOUR,
  OUTCOME_LABEL,
  OUTCOME_ORDER,
  speciesInfo,
  activityLabel,
  ACTIVITY_ORDER,
  STATE_ORDER,
  stateColour,
  SPECIES,
} from '../domain';
import { decadeBins } from '../analysis';
import { el } from '../components/charts';
import { escapeHtml, formatNumber, titleCase } from '../utils/format';

interface Filters {
  q: string;
  st: string;
  sp: string;
  act: string;
  out: string;
  prov: string;
  decade: number | null;
}
type SortKey = 'y' | 'loc' | 'st' | 'sp' | 'out';

export function renderExplorer(root: HTMLElement, ctx: ViewContext): void {
  const all = ctx.data.incidents;
  const f: Filters = { q: '', st: '', sp: '', act: '', out: '', prov: '', decade: null };
  let sortKey: SortKey = 'y';
  let sortDir = -1;

  const speciesOpts = Object.keys(SPECIES).filter((k) => all.some((i) => i.sp === k));
  const activityOpts = ACTIVITY_ORDER.filter((k) => all.some((i) => i.act === k));

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Explore every incident</h1>
      <p class="view-sub">All ${formatNumber(all.length)} records. Filter and sort, click a decade to zoom the timeline, and click any row for the full detail.</p>
    </div>
    <div class="controls">
      <input class="text-input" data-role="q" type="search" placeholder="Search location or species…" aria-label="Search" style="min-width:200px;flex:1" />
      <select data-role="st" aria-label="State"><option value="">All states</option>${STATE_ORDER.map((s) => `<option value="${s}">${s}</option>`).join('')}</select>
      <select data-role="sp" aria-label="Species"><option value="">All species</option>${speciesOpts.map((k) => `<option value="${k}">${escapeHtml(speciesInfo(k).label)}</option>`).join('')}</select>
      <select data-role="act" aria-label="Activity"><option value="">Any activity</option>${activityOpts.map((k) => `<option value="${k}">${escapeHtml(activityLabel(k))}</option>`).join('')}</select>
      <select data-role="out" aria-label="Outcome"><option value="">Any outcome</option>${OUTCOME_ORDER.map((o) => `<option value="${o}">${OUTCOME_LABEL[o]}</option>`).join('')}</select>
      <select data-role="prov" aria-label="Provoked"><option value="">Provoked & unprovoked</option><option value="unprovoked">Unprovoked only</option><option value="provoked">Provoked only</option></select>
      <button class="chip" data-role="clear" hidden>Clear filters ✕</button>
    </div>

    <div class="panel" style="margin-bottom:var(--space-lg)">
      <div class="panel-title">By decade</div>
      <div class="panel-sub">Click a bar to filter to that decade; click it again to clear.</div>
      <div class="chart-scroll" data-role="hist"></div>
    </div>

    <div class="result-count" data-role="count"></div>
    <div class="table-scroll">
      <table class="data">
        <thead><tr>
          <th class="sortable t-num" data-sort="y">Year</th>
          <th class="sortable" data-sort="loc">Location</th>
          <th class="sortable" data-sort="st">State</th>
          <th class="sortable" data-sort="sp">Species</th>
          <th>Activity</th>
          <th class="sortable" data-sort="out">Outcome</th>
        </tr></thead>
        <tbody data-role="rows"></tbody>
      </table>
    </div>
  `;

  const qEl = root.querySelector('[data-role="q"]') as HTMLInputElement;
  const rowsEl = root.querySelector('[data-role="rows"]') as HTMLElement;
  const countEl = root.querySelector('[data-role="count"]') as HTMLElement;
  const histEl = root.querySelector('[data-role="hist"]') as HTMLElement;
  const clearBtn = root.querySelector('[data-role="clear"]') as HTMLButtonElement;

  const passExceptDecade = (it: Incident): boolean => {
    if (f.st && it.st !== f.st) return false;
    if (f.sp && it.sp !== f.sp) return false;
    if (f.act && it.act !== f.act) return false;
    if (f.out && it.out !== (f.out as Outcome)) return false;
    if (f.prov === 'provoked' && it.prov !== true) return false;
    if (f.prov === 'unprovoked' && it.prov !== false) return false;
    if (f.q) {
      const q = f.q.toLowerCase();
      if (!it.loc.toLowerCase().includes(q) && !speciesInfo(it.sp).label.toLowerCase().includes(q)) return false;
    }
    return true;
  };
  const pass = (it: Incident): boolean => {
    if (!passExceptDecade(it)) return false;
    if (f.decade != null) {
      if (it.y == null || Math.floor(it.y / 10) * 10 !== f.decade) return false;
    }
    return true;
  };

  const cmp = (a: Incident, b: Incident): number => {
    let av: string | number;
    let bv: string | number;
    if (sortKey === 'y') { av = a.y ?? -1; bv = b.y ?? -1; }
    else if (sortKey === 'loc') { av = a.loc.toLowerCase(); bv = b.loc.toLowerCase(); }
    else if (sortKey === 'st') { av = a.st ?? ''; bv = b.st ?? ''; }
    else if (sortKey === 'sp') { av = speciesInfo(a.sp).label; bv = speciesInfo(b.sp).label; }
    else { av = OUTCOME_ORDER.indexOf(a.out); bv = OUTCOME_ORDER.indexOf(b.out); }
    if (av < bv) return -sortDir;
    if (av > bv) return sortDir;
    return (b.y ?? 0) - (a.y ?? 0);
  };

  const renderHist = () => {
    histEl.innerHTML = '';
    const bins = decadeBins(all.filter(passExceptDecade));
    if (!bins.length) return;
    const width = Math.max(320, histEl.clientWidth || 900);
    const height = 150;
    const m = { top: 8, right: 8, bottom: 26, left: 8 };
    const plotW = width - m.left - m.right;
    const plotH = height - m.top - m.bottom;
    const max = Math.max(...bins.map((b) => b.count), 1);
    const slot = plotW / bins.length;
    const barW = Math.max(2, slot * 0.82);
    const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });
    bins.forEach((b, i) => {
      const h = Math.max(1, (b.count / max) * plotH);
      const x = m.left + i * slot + (slot - barW) / 2;
      const y = m.top + plotH - h;
      const active = f.decade === b.lo;
      const bar = el('rect', {
        x, y, width: barW, height: h, rx: 2,
        fill: active ? OUTCOME_COLOUR.fatal : '#2b8994',
        style: 'cursor:pointer',
        'data-tip': `${b.label}: ${formatNumber(b.count)} incidents`,
        opacity: f.decade != null && !active ? 0.4 : 1,
      });
      bar.addEventListener('click', () => {
        f.decade = active ? null : b.lo;
        update();
      });
      svg.appendChild(bar);
      if (i % 2 === 0 || bins.length <= 14) {
        const lbl = el('text', { class: 'axis-label', x: x + barW / 2, y: height - 8, 'text-anchor': 'middle' });
        lbl.textContent = b.label.slice(0, 4);
        svg.appendChild(lbl);
      }
    });
    histEl.appendChild(svg);
  };

  const renderRows = () => {
    const list = all.filter(pass).sort(cmp);
    countEl.textContent = `${formatNumber(list.length)} of ${formatNumber(all.length)} incidents`;
    const anyFilter = !!(f.q || f.st || f.sp || f.act || f.out || f.prov || f.decade != null);
    clearBtn.hidden = !anyFilter;
    rowsEl.innerHTML = list
      .slice(0, 1500)
      .map((it) => {
        const sp = speciesInfo(it.sp);
        return `<tr data-id="${it.id}">
          <td class="t-num">${it.y ?? '—'}</td>
          <td class="loc-cell">${escapeHtml(it.loc ? titleCase(it.loc) : '—')}</td>
          <td>${it.st ? `<span class="pill" style="background:${stateColour(it.st)}">${escapeHtml(it.st)}</span>` : '—'}</td>
          <td><span class="sp-dot" style="background:${sp.colour}"></span>${escapeHtml(sp.label)}</td>
          <td>${escapeHtml(activityLabel(it.act))}</td>
          <td><span class="out-badge" style="color:${OUTCOME_COLOUR[it.out]};border-color:${OUTCOME_COLOUR[it.out]}">${OUTCOME_LABEL[it.out]}</span></td>
        </tr>`;
      })
      .join('');
    root.querySelectorAll('[data-sort]').forEach((th) => {
      const key = th.getAttribute('data-sort');
      th.innerHTML = th.textContent!.replace(/ [▲▼]$/, '') + (key === sortKey ? (sortDir === 1 ? ' ▲' : ' ▼') : '');
    });
  };

  const update = () => {
    renderHist();
    renderRows();
  };

  // Wire controls
  let timer: number | undefined;
  qEl.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      f.q = qEl.value;
      update();
    }, 300);
  });
  for (const key of ['st', 'sp', 'act', 'out', 'prov'] as const) {
    (root.querySelector(`[data-role="${key}"]`) as HTMLSelectElement).addEventListener('change', (e) => {
      f[key] = (e.target as HTMLSelectElement).value;
      update();
    });
  }
  clearBtn.addEventListener('click', () => {
    f.q = ''; f.st = ''; f.sp = ''; f.act = ''; f.out = ''; f.prov = ''; f.decade = null;
    qEl.value = '';
    for (const key of ['st', 'sp', 'act', 'out', 'prov'] as const) (root.querySelector(`[data-role="${key}"]`) as HTMLSelectElement).value = '';
    update();
  });
  root.querySelectorAll('[data-sort]').forEach((th) =>
    th.addEventListener('click', () => {
      const key = th.getAttribute('data-sort') as SortKey;
      if (sortKey === key) sortDir = -sortDir;
      else { sortKey = key; sortDir = key === 'loc' || key === 'st' ? 1 : -1; }
      renderRows();
    }),
  );
  rowsEl.addEventListener('click', (e) => {
    const tr = (e.target as Element).closest('[data-id]');
    if (tr) ctx.openIncident(Number(tr.getAttribute('data-id')));
  });

  window.addEventListener('resize', renderHist, { passive: true });
  update();
}
